/**
 * DPDP Act (2023) aligned policy templates for recommended organizational policies.
 * Placeholders: {{organization_name}}, {{effective_date}}
 */

function fillTemplate(text, vars) {
  return String(text || "")
    .replace(/\{\{organization_name\}\}/g, vars.organizationName || "the Organization")
    .replace(/\{\{effective_date\}\}/g, vars.effectiveDate || new Date().toLocaleDateString("en-IN"));
}

const POLICY_DEFINITIONS = [
  {
    id: "personal-data-protection",
    title: "Personal Data Protection Policy",
    subtitle: "Section 8 — Security safeguards & lawful processing",
    actRef: "DPDP Act §8",
    clauses: [
      {
        title: "Purpose & Scope",
        content:
          "{{organization_name}} adopts this Personal Data Protection Policy to ensure lawful, fair, and transparent processing of digital personal data in compliance with the Digital Personal Data Protection Act, 2023 (DPDP Act). This policy applies to all employees, contractors, and systems that collect, store, or process personal data.",
      },
      {
        title: "Lawful Processing",
        content:
          "Personal data shall be processed only for lawful purposes with valid consent, contractual necessity, or other grounds permitted under the DPDP Act. Processing must be limited to what is necessary for the specified purpose.",
      },
      {
        title: "Reasonable Security Safeguards",
        content:
          "{{organization_name}} shall implement appropriate technical and organizational measures — including access controls, encryption, logging, and periodic security assessments — to protect personal data against unauthorized access, disclosure, alteration, or destruction.",
      },
      {
        title: "Data Accuracy",
        content:
          "Reasonable efforts shall be made to ensure personal data is accurate and kept up to date. Data principals may request correction of inaccurate or incomplete data.",
      },
      {
        title: "Roles & Accountability",
        content:
          "The Data Protection Officer (or designated privacy lead) is responsible for policy oversight, training, and coordination with the Data Protection Board. All staff must complete privacy awareness training annually.",
      },
    ],
  },
  {
    id: "privacy-notice",
    title: "Privacy Notice Policy",
    subtitle: "Section 5 — Notice to data principals",
    actRef: "DPDP Act §5",
    clauses: [
      {
        title: "Transparency Requirement",
        content:
          "Before or at the time of collecting personal data, {{organization_name}} shall provide a clear, accessible privacy notice in English and, where practicable, in regional languages.",
      },
      {
        title: "Notice Contents",
        content:
          "Each notice must describe: (a) the personal data collected; (b) the purpose of processing; (c) how data principals may exercise their rights; (d) the mechanism to withdraw consent; and (e) contact details for grievance redressal.",
      },
      {
        title: "Updates & Version Control",
        content:
          "Material changes to processing activities require an updated notice. Data principals shall be notified of significant changes through appropriate channels before changes take effect.",
      },
    ],
  },
  {
    id: "consent-management",
    title: "Consent Management Policy",
    subtitle: "Section 6 — Free, specific, informed consent",
    actRef: "DPDP Act §6",
    clauses: [
      {
        title: "Valid Consent Standards",
        content:
          "Consent must be free, specific, informed, and unambiguous. Pre-ticked boxes, bundled consent for unrelated purposes, or dark patterns are prohibited.",
      },
      {
        title: "Consent Records",
        content:
          "{{organization_name}} shall maintain auditable records of consent including timestamp, purpose, channel, and the exact notice presented to the data principal.",
      },
      {
        title: "Withdrawal of Consent",
        content:
          "Data principals may withdraw consent as easily as it was given. Upon withdrawal, processing based solely on that consent shall cease unless another lawful basis applies.",
      },
      {
        title: "Consent for Children",
        content:
          "Verifiable parental or guardian consent is required before processing personal data of children, in accordance with DPDP Act Section 9 and applicable rules.",
      },
    ],
  },
  {
    id: "data-principal-rights",
    title: "Data Principal Rights Policy",
    subtitle: "Sections 11–14 — Access, correction, erasure, grievance",
    actRef: "DPDP Act §11–14",
    clauses: [
      {
        title: "Right to Access",
        content:
          "Data principals may request a summary of their personal data being processed and the processing activities undertaken, within timelines prescribed under the DPDP Act.",
      },
      {
        title: "Correction & Erasure",
        content:
          "{{organization_name}} shall enable requests for correction of inaccurate data and erasure when consent is withdrawn or processing is no longer necessary, subject to legal retention requirements.",
      },
      {
        title: "Nomination of Rights",
        content:
          "Data principals may nominate another individual to exercise their rights in the event of death or incapacity, as permitted under the Act.",
      },
      {
        title: "Grievance Redressal",
        content:
          "A designated grievance officer shall acknowledge and resolve complaints within prescribed timelines. Unresolved grievances may be escalated to the Data Protection Board.",
      },
    ],
  },
  {
    id: "retention-erasure",
    title: "Data Retention & Erasure Policy",
    subtitle: "Section 8(7) & Section 12 — Storage limitation",
    actRef: "DPDP Act §8(7), §12",
    clauses: [
      {
        title: "Retention Schedules",
        content:
          "Personal data shall not be retained longer than necessary for the stated purpose. {{organization_name}} maintains retention schedules aligned with legal, regulatory, and business requirements.",
      },
      {
        title: "Secure Disposal",
        content:
          "When retention periods expire, personal data shall be securely deleted, anonymized, or archived in accordance with approved disposal procedures.",
      },
      {
        title: "Erasure on Request",
        content:
          "Erasure requests shall be processed unless retention is required by law or for establishment, exercise, or defence of legal claims.",
      },
    ],
  },
  {
    id: "breach-notification",
    title: "Personal Data Breach Notification Policy",
    subtitle: "Section 8(6) — Breach reporting obligations",
    actRef: "DPDP Act §8(6)",
    clauses: [
      {
        title: "Breach Definition",
        content:
          "A personal data breach is any unauthorized access, acquisition, use, disclosure, alteration, or loss of personal data that compromises its confidentiality, integrity, or availability.",
      },
      {
        title: "Internal Response",
        content:
          "{{organization_name}} shall maintain an incident response plan including containment, forensic investigation, impact assessment, and remediation within 72 hours of discovery.",
      },
      {
        title: "Regulatory & Individual Notification",
        content:
          "Affected data principals and the Data Protection Board shall be notified when a breach is likely to cause harm, in the manner and timeframe prescribed under the DPDP Act and applicable rules.",
      },
    ],
  },
  {
    id: "cross-border-transfer",
    title: "Cross-Border Data Transfer Policy",
    subtitle: "Section 16 — Transfer outside India",
    actRef: "DPDP Act §16",
    clauses: [
      {
        title: "Transfer Restrictions",
        content:
          "Personal data may be transferred outside India only to countries or territories notified by the Central Government, or under contractual safeguards approved under the DPDP Act.",
      },
      {
        title: "Due Diligence",
        content:
          "{{organization_name}} shall assess the adequacy of protection in the recipient jurisdiction and implement standard contractual clauses or binding corporate rules where required.",
      },
      {
        title: "Transfer Register",
        content:
          "All cross-border transfers shall be documented in the transfer register including recipient, legal basis, safeguards, and data categories involved.",
      },
    ],
  },
  {
    id: "vendor-processor",
    title: "Data Processor & Vendor Management Policy",
    subtitle: "Section 8(8) & Section 17 — Processor obligations",
    actRef: "DPDP Act §8(8), §17",
    clauses: [
      {
        title: "Processor Agreements",
        content:
          "All data processors and vendors handling personal data on behalf of {{organization_name}} must execute data processing agreements specifying scope, security, sub-processing, and breach notification obligations.",
      },
      {
        title: "Vendor Due Diligence",
        content:
          "Privacy and security assessments shall be conducted before onboarding vendors and periodically thereafter, with heightened scrutiny for high-risk processing.",
      },
      {
        title: "Sub-Processing Controls",
        content:
          "Sub-processors require prior authorization. {{organization_name}} retains accountability for processor compliance throughout the vendor lifecycle.",
      },
    ],
  },
  {
    id: "children-data",
    title: "Children's Data Protection Policy",
    subtitle: "Section 9 — Verifiable parental consent",
    actRef: "DPDP Act §9",
    clauses: [
      {
        title: "Age Verification",
        content:
          "{{organization_name}} shall implement age-verification mechanisms before collecting personal data from individuals who may be children, as defined under applicable rules.",
      },
      {
        title: "Parental Consent",
        content:
          "Processing of children's personal data requires verifiable consent from a parent or lawful guardian. Tracking, behavioural monitoring, and targeted advertising directed at children is prohibited unless explicitly permitted.",
      },
    ],
  },
  {
    id: "grievance-redressal",
    title: "Grievance Redressal Policy",
    subtitle: "Section 13 — Complaint handling framework",
    actRef: "DPDP Act §13",
    clauses: [
      {
        title: "Grievance Officer",
        content:
          "{{organization_name}} appoints a Grievance Officer reachable at a published email and postal address. Contact details appear in all privacy notices and on the organization website.",
      },
      {
        title: "Resolution Timelines",
        content:
          "Complaints shall be acknowledged within 24 hours and resolved within timelines prescribed under the DPDP Rules, typically within 30 days unless extended with justification.",
      },
      {
        title: "Escalation",
        content:
          "If unsatisfied with the resolution, data principals may approach the Data Protection Board of India through the prescribed mechanism.",
      },
    ],
  },
];

export function getDpdpPolicies({ organizationName, effectiveDate } = {}) {
  const vars = {
    organizationName: organizationName || "the Organization",
    effectiveDate:
      effectiveDate ||
      new Date().toLocaleDateString("en-IN", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
  };

  return POLICY_DEFINITIONS.map((policy) => ({
    ...policy,
    effectiveDate: vars.effectiveDate,
    clauses: policy.clauses.map((clause) => ({
      title: clause.title,
      content: fillTemplate(clause.content, vars),
    })),
    summary: policy.clauses[0]
      ? fillTemplate(policy.clauses[0].content, vars).slice(0, 160) + "…"
      : "",
  }));
}

export function policiesToPdfSections(policies) {
  return policies.map((policy) => ({
    heading: policy.title,
    rows: [
      { label: "DPDP Reference", value: policy.actRef },
      { label: "Effective Date", value: policy.effectiveDate },
    ],
    qa: policy.clauses.map((clause) => ({
      question: clause.title,
      answer: clause.content,
    })),
  }));
}

export const DPDP_POLICY_COUNT = POLICY_DEFINITIONS.length;
